import styled from 'styled-components';
import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import { hexa, marginReplacer } from 'styles/utils';
import NotificationBadge from 'components/NotificationBadge';

const outlineColor = {
  primary: 'primary',
  secondary: 'wfGrey700',
  tertiary: 'wfBlack',
  destructive: 'wfOrange',
  warning: 'wfYellow',
  success: 'wfGreen',
  minimal: 'wfGrey300',
};

const fontColor = {
  primary: 'wfWhite',
  secondary: 'wfBlack',
  tertiary: 'wfWhite',
  destructive: 'wfBlack',
  warning: 'wfBlack',
  success: 'wfBlack',
  minimal: 'wfBlack',
};

const fontColorHover = {
  primary: 'wfWhite',
  secondary: 'wfWhite',
  tertiary: 'wfWhite',
  destructive: 'wfBlack',
  warning: 'wfBlack',
  success: 'wfBlack',
  minimal: 'wfWhite',
};

const backgroundColor = {
  primary: 'primary',
  secondary: 'wfGrey200',
  tertiary: 'wfBlack',
  destructive: 'wfOrange',
  warning: 'wfYellow',
  success: 'wfGreen',
  minimal: 'wfGrey300',
};

const backgroundColorHover = {
  primary: 'primaryLight',
  secondary: 'wfBlack',
  tertiary: 'wfGrey700',
  destructive: 'wfOrange500',
  warning: 'wfYellow500',
  success: 'wfGreen500',
  minimal: 'wfGrey300',
};

export const ButtonStyled = styled.button`
  box-shadow: ${props => props.shadow && props.theme.shadow.sm};
  text-decoration: ${props => props.underlined && 'underline'};
  width: ${props => props.fullWidth && '100%'};
  min-width: ${props =>
    props.text && props.appearance === 'default'
      ? '8em'
      : props.appearance === 'hero' && '17em'};
  white-space: nowrap;
  display: inline-flex;
  justify-content: ${props =>
    props.appearance === 'hero' && props.icon ? 'space-between' : 'center'};
  cursor: pointer;
  margin: ${props => (props.margin ? marginReplacer(props.margin) : '0')};
  padding: ${props =>
    props.appearance === 'hero'
      ? props.icon
        ? '1.5em'
        : '1.5em 3em'
      : props.appearance === 'plain'
      ? '0.5em 0'
      : props.text
      ? props.size === 'xsmall'
        ? '0.584em 1.3em'
        : props.size === 'small'
        ? '0.643em 1.3em'
        : props.size === 'default'
        ? '0.813em 1.3em'
        : '0.8em 1.3em'
      : props.size === 'xsmall'
      ? '0.584em'
      : props.size === 'small'
      ? '0.569em'
      : props.size === 'default'
      ? '0.763em'
      : '0.763em'};
  font-size: ${props =>
    props.theme.font.size[props.size] || props.theme.font.size.default};
  font-weight: ${props =>
    props.removeBold
      ? props.theme.font.weight.regular
      : props.appearance === 'plain' && props.theme.font.weight.xbold};
  line-height: ${props => (props.text ? 1 : 0)};
  color: ${props =>
    props.customColor
      ? props.customColor
      : props.active
      ? props.theme.color.primary
      : props.outline || props.appearance === 'plain'
      ? props.theme.color[outlineColor[props.variant]]
      : props.negativeVersion
      ? props.theme.color.wfWhite
      : props.customColor
      ? props.customColor
      : props.customBgColor
      ? props.theme.color.wfWhite
      : props.theme.color[fontColor[props.variant]]};
  border: ${props =>
    props.appearance === 'plain'
      ? 'none'
      : `1px solid ${
          props.active
            ? props.theme.color.primary
            : props.customBgColor
            ? props.customBgColor
            : props.variant === 'secondary'
            ? props.outline
              ? props.theme.color.wfGrey700
              : props.theme.color.wfGrey200
            : props.theme.color[outlineColor[props.variant]]
        }`};
  border-radius: ${props =>
    props.square
      ? null
      : props.appearance === 'hero'
      ? '3em'
      : props.appearance === 'default' &&
        (props.text ? props.theme.borderRadius.large : '50%')};
  content: '${props => props.square}';
  background-color: ${props =>
    props.transparent
      ? 'transparent'
      : props.active || props.outline
      ? props.theme.color.wfWhite
      : props.appearance === 'plain'
      ? 'transparent'
      : props.customBgColor
      ? props.customBgColor
      : props.theme.color[backgroundColor[props.variant]] || 'transparent'};
  transition: all 0.2s ease;
  ${props =>
    props.disabled &&
    `
    opacity: .5;
    pointer-events: none;
    user-select: none;
  `};
  /* Overriding normalize rules for webkit */
  &&& {
    -webkit-appearance: none;
  }
  &:hover {
    text-decoration: ${props =>
      props.appearance === 'plain' &&
      (props.underlined ? 'none' : 'underline')};
    background-color: ${props =>
      props.active || props.appearance === 'plain'
        ? 'transparent'
        : props.outline && props.variant === 'primary'
        ? props.theme.color.wfBlue100
        : props.outline
        ? props.variant === 'secondary'
          ? props.theme.color.primary
          : props.theme.color[backgroundColor[props.variant]]
        : props.customBgColor
        ? hexa(props.customBgColor, 0.8)
        : props.theme.color[backgroundColorHover[props.variant]]};
    border-color: ${props =>
      props.active
        ? null
        : props.outline
        ? props.variant === 'secondary'
          ? props.theme.color.primary
          : props.theme.color[backgroundColor[props.variant]]
        : props.customBgColor
        ? 'transparent'
        : props.theme.color[backgroundColorHover[props.variant]]};
    color: ${props =>
      props.appearance === 'plain' || props.active
        ? null
        : props.outline && props.variant === 'primary'
        ? props.theme.color.primary
        : props.theme.color[fontColorHover[props.variant]]};
  }
`;

export const SpinnerStyled = styled(Spinner)`
  margin-right: 0.5em;
`;

export const IconWrapper = styled.div`
  ${props =>
    props.withText &&
    `margin-${props.iconPosition === 'right' ? 'left' : 'right'}: ${
      props.appearance === 'plain'
        ? '.5em'
        : props.appearance === 'hero'
        ? '3em'
        : '1em'
    };`};

  order: ${props => props.withText && (props.iconPosition === 'left' ? 0 : 1)};
`;

export const IconStyled = styled(Icon)`
  width: ${props =>
    !props.withText &&
    (props.size === 'medium'
      ? '1.2em'
      : props.size === 'small'
      ? '1.15em'
      : props.size === 'xsmall'
      ? ''
      : '1.1em')};
  height: ${props =>
    !props.withText &&
    (props.size === 'medium'
      ? '1.2em'
      : props.size === 'small'
      ? '1.15em'
      : props.size === 'xsmall'
      ? ''
      : '1.1em')};
`;

export const IconNotificationBadge = styled(NotificationBadge)`
  vertical-align: ${props => props.withText && 'top'};
`;
